/* eslint-disable restrict-imports/restrict-imports */
import React from 'react';
import { makeStyles } from 'design-system/product/theme';
import Markdown from '@scale/frontend-lib/markdown';

import { ExampleLabelPreview } from 'dashboard/components/SelfServe/Onboarding/Instruction/components/ExampleLabelPreview';
import { StaticAssetPreview } from 'dashboard/components/SelfServe/Onboarding/Instruction/components/StaticAssetPreview';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IInstructionExampleSampleJson } from '@scale/shared/projectInstruction/types';
import { useProjectInstructionContext } from '../projectInstructionContext';
import { SearchResultItemProps } from '../types';
import _ from 'lodash';
import ReactPlayer from 'react-player';
import { InstructionExampleSampleType } from '@scale/shared/defaults';

const Sample = ({
  sample,
  index,
  fullWidth,
}: {
  sample: IInstructionExampleSampleJson | SearchResultItemProps;
  index: number;
  fullWidth?: boolean; // fullwidth is used for contextual trigger popper, not limited to side panel view
}) => {
  const classes = useStyles();
  const { type, description, linkedTask, screenshotUrl, staticAsset, isNew, videoUrl, didChange } =
    sample;

  const { isMobileView } = useProjectInstructionContext();
  const instructionType = type as InstructionExampleSampleType | undefined;
  const isGoodExample = instructionType === InstructionExampleSampleType.Good;
  const hide = didChange !== undefined;
  return (
    <div
      className={cx(classes.taskExampleWrapper, {
        [classes.goodExample]: instructionType === InstructionExampleSampleType.Good,
        [classes.badExample]: instructionType === InstructionExampleSampleType.Bad,
        [classes.neutralExample]: instructionType === InstructionExampleSampleType.Neutral,
        [classes.fullWidth]: fullWidth || isMobileView,
        [classes.hidden]: !didChange && hide,
      })}
    >
      {staticAsset && !_.isEmpty(staticAsset) && (
        <StaticAssetPreview
          key={sample.title}
          readOnly={true}
          index={index}
          preventOpen={false}
          handleDelete={() => {}}
          staticAsset={staticAsset}
        />
      )}
      {linkedTask && (
        <ExampleLabelPreview
          taskId={linkedTask}
          caption={description}
          isGoodExample={isGoodExample}
          readOnly={true}
          index={index}
        />
      )}
      {videoUrl && <ReactPlayer controls width="100%" url={videoUrl} />}
      {instructionType &&
        [InstructionExampleSampleType.Good, InstructionExampleSampleType.Bad].includes(
          instructionType,
        ) && (
          <div className={classes.correctness}>
            <FontAwesomeIcon
              icon={isGoodExample ? 'check-circle' : 'minus-circle'}
              color={isGoodExample ? '#4FAD75' : '#B71C1C'}
            />
            {isGoodExample ? ' Correct' : ' Incorrect'}
          </div>
        )}
      {(isNew || didChange) && <strong className={classes.newBadge}>New!</strong>}

      {screenshotUrl && <img src={screenshotUrl} />}
      <div>
        <p
          className={classes.description}
          dangerouslySetInnerHTML={{
            __html: Markdown.render(description || ''),
          }}
        />
      </div>
    </div>
  );
};

const useStyles = makeStyles(() => ({
  description: {
    padding: '0 5px 0px 5px',
    display: 'block',
    overflow: 'scroll',
    '&::-webkit-scrollbar': { height: 0 },
    '& p': {
      margin: 0,
    },
    fontStyle: 'italic',
  },
  taskExampleWrapper: {
    position: 'relative',
    width: 170,
    border: '1px solid red',
    margin: '10px 20px 10px 0',
    borderRadius: 10,
    overflow: 'hidden',
    '& img': {
      height: 150,
    },
    '&:hover': {
      opacity: 0.7,
    },
  },
  correctness: { fontSize: 13, padding: 5 },
  goodExample: {
    border: '2px solid #4FAD75',
    background: '#F9FCFB',
  },
  badExample: {
    border: '2px solid #B71C1C',
    background: '#FFF4F6',
  },
  neutralExample: {
    border: '2px solid #E0E0E0',
    background: '#F9F9F9',
  },

  fullWidth: {
    width: '100%',
    padding: 0,
  },
  hidden: {
    opacity: 0.25,
  },
  newBadge: {
    borderRadius: '1000px',
    padding: '4px 12px',
    position: 'absolute',
    left: '4px',
    top: '4px',
    fontSize: '8px',
    background: 'var(--color-RemoGreen)',
    userSelect: 'none',
    cursor: 'pointer',
  },
}));

export default React.memo(Sample);
